import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AccountSettingsScreen } from "./AccountSettingsScreen";
import { AccessSettingsScreen } from "./AccessSettingsScreen";
import { AuditSettingsScreen } from "./AuditSettingsScreen";
import { EntitlementsSettingsScreen } from "./EntitlementsSettingsScreen";
import { RbacSettingsScreen } from "./RbacSettingsScreen";
import { WorkspaceSettingsScreen } from "./WorkspaceSettingsScreen";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const GOVERNANCE_TABS = [
  { id: "account", label: "Account" },
  { id: "workspace", label: "Workspace" },
  { id: "access", label: "Access" },
  { id: "rbac", label: "RBAC" },
  { id: "entitlements", label: "Entitlements" },
  { id: "audit", label: "Audit" },
] as const;

type GovernanceTabId = (typeof GOVERNANCE_TABS)[number]["id"];

const DEFAULT_TAB: GovernanceTabId = "workspace";

const LEGACY_TAB_BY_PATH = new Map<string, GovernanceTabId>([
  ["/settings/account", "account"],
  ["/workspace_settings", "workspace"],
  ["/settings/access", "access"],
  ["/settings/rbac", "rbac"],
  ["/settings/entitlements", "entitlements"],
  ["/settings/audit", "audit"],
]);

function resolveTab(value: string | null | undefined): GovernanceTabId {
  const candidate = (value ?? "").trim().toLowerCase();
  return (GOVERNANCE_TABS.find((tab) => tab.id === candidate)?.id ?? DEFAULT_TAB) as GovernanceTabId;
}

export function GovernanceScreen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabParam = searchParams.get("tab");
  const legacyTab = LEGACY_TAB_BY_PATH.get(location.pathname);
  const activeTab = resolveTab(tabParam ?? legacyTab ?? DEFAULT_TAB);

  useEffect(() => {
    if (!legacyTab && tabParam === activeTab) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.set("tab", activeTab);
    navigate(`/governance?${params.toString()}`, { replace: true });
  }, [activeTab, legacyTab, navigate, searchParams, tabParam]);

  const handleTabChange = (nextTab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", nextTab);
    navigate(`/governance?${params.toString()}`);
  };

  return (
    <div className="governance-screen flex flex-col gap-6">
      <div className="governance-screen__header">
        <h1 className="text-2xl font-semibold text-foreground">Governance</h1>
        <p className="text-sm text-muted-foreground">
          Manage workspace configuration, access controls, entitlements, and audit trails.
        </p>
      </div>
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="governance-screen__tabs">
          {GOVERNANCE_TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="account">
          <AccountSettingsScreen />
        </TabsContent>
        <TabsContent value="workspace">
          <WorkspaceSettingsScreen />
        </TabsContent>
        <TabsContent value="access">
          <AccessSettingsScreen />
        </TabsContent>
        <TabsContent value="rbac">
          <RbacSettingsScreen />
        </TabsContent>
        <TabsContent value="entitlements">
          <EntitlementsSettingsScreen />
        </TabsContent>
        <TabsContent value="audit">
          <AuditSettingsScreen />
        </TabsContent>
      </Tabs>
    </div>
  );
}
